/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import TableWrapper from 'components/Ui/Tanstack/TableWrapper'
import TableHeadWrapper from 'components/Ui/Tanstack/TableHeadWrapper'
import TableBody from 'components/Ui/Tanstack/TableBody'
import TableFoot from 'components/Ui/Tanstack/TableFoot'
import UiTanstackTableHeaderGroups from 'components/Ui/Tanstack/UiTanstackTableHeaderGroups'
import UiTanstackTableColGroup from 'components/Ui/Tanstack/UiTanstackTableColGroup'
import UiTableMassActionsBar from 'components/Ui/Tanstack/TableMassActions/UiTableMassActionsBar'
import UiTanstackBatchSelectControl from 'components/Ui/Tanstack/UiTanstackBatchSelectControl'
import { getBatchHeaderSelectMeta } from 'components/Ui/Tanstack/batchHeaderSelect'
import { sumTableColumnWidthsPx, TableColumnMeta } from 'utils/table/tableColumnWidths'
import { useTranslation } from 'react-i18next'
import { Header, Row } from '@tanstack/react-table'
import { TableViewDropdownProvider } from 'components/Ui/TableViewDropdownContext'
import { useTanstackTableScrollRestore } from 'utils/hooks/useTanstackTableScrollRestore'
import { TableInitBase } from 'utils/hooks/useTable'

interface UiTanstackTableProps<TData = any> {
    tableInit: TableInitBase
    onRowClick?: (row: Row<TData>) => void
    isTableStateSame?: boolean
    filterableHeaderIds?: string[]
    renderHeaderFilter?: (header: Header<TData, unknown>, tableInit: TableInitBase) => React.ReactNode
    renderHeaderTitleProps?: (header: Header<TData, unknown>, tableInit: TableInitBase) => Record<string, unknown>
    paginationIdPrefix?: string
    tableMinHeightClass?: string
    tableClassName?: string
    className?: string
    hidePagination?: boolean
    renderFooter?: (tableInit: TableInitBase) => React.ReactNode
    isBatchMode?: boolean
    renderBatchHeaderControl?: (
        header: Header<TData, unknown>,
        tableInit: TableInitBase
    ) => React.ReactNode
}

const UiTanstackTable: React.FC<UiTanstackTableProps> = ({
    tableInit,
    onRowClick,
    isTableStateSame,
    filterableHeaderIds: _filterableHeaderIds = [],
    renderHeaderFilter,
    renderHeaderTitleProps,
    paginationIdPrefix,
    tableMinHeightClass,
    tableClassName,
    className,
    hidePagination = false,
    renderFooter,
    isBatchMode = false,
    renderBatchHeaderControl,
}) => {
    const { t } = useTranslation()
    const shouldShowSkeleton = tableInit.showSkeletonLoading === true
    const { tableScrollKey, rememberRowClick } = useTanstackTableScrollRestore({
        table: tableInit.table,
        tableId: (tableInit as { tableId?: string }).tableId,
    })
    // State to track which column search inputs are visible
    const [searchVisibility, setSearchVisibility] = React.useState<Record<string, boolean>>({})
    const [batchHeaderValues, setBatchHeaderValues] = React.useState<Record<string, string>>({})
    const [batchHeaderLoading, setBatchHeaderLoading] = React.useState<Record<string, boolean>>({})

    const handleToggleSearch = (headerId: string) => {
        setSearchVisibility((prev) => ({
            ...prev,
            [headerId]: !prev[headerId],
        }))
    }

    const columnFiltersVisible = tableInit.columnFiltersVisible ?? false
    const prevColumnFiltersVisibleRef = React.useRef(columnFiltersVisible)

    React.useLayoutEffect(() => {
        if (prevColumnFiltersVisibleRef.current === columnFiltersVisible) {
            return
        }

        prevColumnFiltersVisibleRef.current = columnFiltersVisible

        if (!tableScrollKey) {
            return
        }

        const tableWrapper = document.querySelector<HTMLElement>(`[data-table-scroll-key="${tableScrollKey}"]`)
        const scrollContainer = tableWrapper?.querySelector<HTMLElement>('div.overflow-auto')

        if (scrollContainer) {
            scrollContainer.scrollLeft = 0
        }
    }, [columnFiltersVisible, tableScrollKey])

    const handleRowInteraction = (row: Row<any>) => {
        rememberRowClick(row)
    }

    const handleRowClick = (row: Row<any>) => {
        if (onRowClick) {
            onRowClick(row)
        }
    }

    React.useEffect(() => {
        if (tableInit.hasSelection) {
            return
        }

        setBatchHeaderValues({})
        setBatchHeaderLoading({})
    }, [tableInit.hasSelection])

    const defaultRenderBatchHeaderControl = React.useCallback(
        (header: Header<any, unknown>, currentTableInit: TableInitBase) => {
            if (!currentTableInit.hasSelection) {
                return null
            }

            const batchHeaderSelect = getBatchHeaderSelectMeta(header)

            if (!batchHeaderSelect) {
                return null
            }

            const context = {
                header,
                tableInit: currentTableInit,
                selectedRows: currentTableInit.selectedRows,
                selectedIds: currentTableInit.selectedRowIds,
                clearSelection: currentTableInit.clearSelection,
            }

            const options =
                typeof batchHeaderSelect.options === 'function'
                    ? batchHeaderSelect.options(context)
                    : batchHeaderSelect.options

            if (!Array.isArray(options) || options.length === 0) {
                return null
            }

            const selectedValue = batchHeaderValues[header.id] ?? ''
            const isApplying = batchHeaderLoading[header.id] === true
            const isDisabled =
                isApplying || batchHeaderSelect.disabled?.(context) === true

            return (
                <UiTanstackBatchSelectControl
                    options={options}
                    selectedValue={selectedValue}
                    onSelectedValueChange={(value) =>
                        setBatchHeaderValues((prev) => ({
                            ...prev,
                            [header.id]: value,
                        }))
                    }
                    onApply={async () => {
                        if (!selectedValue || isApplying) {
                            return
                        }

                        setBatchHeaderLoading((prev) => ({
                            ...prev,
                            [header.id]: true,
                        }))

                        try {
                            await batchHeaderSelect.onApply(selectedValue, context)
                            setBatchHeaderValues((prev) => ({
                                ...prev,
                                [header.id]: '',
                            }))
                        } finally {
                            setBatchHeaderLoading((prev) => ({
                                ...prev,
                                [header.id]: false,
                            }))
                        }
                    }}
                    disabled={isDisabled}
                    selectPlaceholder={batchHeaderSelect.selectPlaceholder}
                    applyLabel={batchHeaderSelect.applyLabel ?? t('Apply')}
                />
            )
        },
        [batchHeaderLoading, batchHeaderValues, t]
    )

    const resolvedRenderBatchHeaderControl = React.useCallback(
        (header: Header<any, unknown>, currentTableInit: TableInitBase) => {
            const override = renderBatchHeaderControl?.(header, currentTableInit)

            return override ?? defaultRenderBatchHeaderControl(header, currentTableInit)
        },
        [defaultRenderBatchHeaderControl, renderBatchHeaderControl]
    )

    const visibleLeafColumns = tableInit.table.getVisibleLeafColumns()
    const tableMinContentWidthPx = React.useMemo(
        () =>
            sumTableColumnWidthsPx(
                visibleLeafColumns.map((column) => ({
                    id: column.id,
                    meta: column.columnDef.meta as TableColumnMeta | undefined,
                }))
            ),
        [visibleLeafColumns]
    )
    const visibleColumnsCount = visibleLeafColumns.length || 1

    return (
        <TableViewDropdownProvider tableInit={tableInit}>
        <TableWrapper
            tableMinHeightClass={tableMinHeightClass}
            tableClassName={tableClassName}
            className={clsx('UiTanstackTable', className)}
            tableScrollKey={tableScrollKey}
            tableMinContentWidthPx={tableMinContentWidthPx > 0 ? tableMinContentWidthPx : undefined}
            topBar={<UiTableMassActionsBar tableInit={tableInit} />}
            footer={
                <div className="flex flex-col">
                    {renderFooter &&
                        (shouldShowSkeleton ? (
                            <div className="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 px-4 py-3">
                                <div className="flex justify-end gap-8">
                                    <div className="h-4 w-28 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                                    <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                                </div>
                            </div>
                        ) : (
                            renderFooter(tableInit)
                        ))}
                    {!hidePagination && (
                        <TableFoot
                            colSpan={visibleColumnsCount}
                            tableInit={tableInit}
                            paginationIdPrefix={paginationIdPrefix}
                        />
                    )}
                </div>
            }
        >
            <UiTanstackTableColGroup columns={visibleLeafColumns} />
            <TableHeadWrapper>
                <UiTanstackTableHeaderGroups
                    headerGroups={tableInit.table.getHeaderGroups()}
                    tableInit={tableInit}
                    filterableHeaderIds={_filterableHeaderIds}
                    renderHeaderFilter={renderHeaderFilter}
                    renderHeaderTitleProps={renderHeaderTitleProps}
                    searchVisibility={searchVisibility}
                    onToggleSearch={handleToggleSearch}
                    translate={t}
                    isBatchMode={isBatchMode || tableInit.hasSelection}
                    renderBatchHeaderControl={resolvedRenderBatchHeaderControl}
                />
            </TableHeadWrapper>
            <TableBody
                colSpan={visibleColumnsCount}
                decorationId={'select'}
                tableInit={tableInit}
                isTableStateSame={isTableStateSame}
                onRowInteraction={handleRowInteraction}
                onRowClick={handleRowClick}
            />
        </TableWrapper>
        </TableViewDropdownProvider>
    )
}

export default UiTanstackTable
